import React, { useState, useRef, useEffect } from "react";
import { Mic, MicOff, X, Loader2, RotateCcw, ChevronDown, ChevronUp } from "lucide-react";
import { api } from "../lib/api";
import { toast } from "sonner";

// ── Constants ─────────────────────────────────────────────────────────────────
const FILLER_PATTERNS = [
  "\\bum\\b", "\\buh\\b", "\\blike\\b", "\\byou know\\b",
  "\\bbasically\\b", "\\bliterally\\b", "\\bsort of\\b", "\\bkind of\\b",
  "\\bi mean\\b", "\\bright so\\b", "\\bokay so\\b", "\\bso um\\b",
  "\\band uh\\b", "\\bactually\\b", "\\bwhatever\\b",
];

function detectFillers(text) {
  const lower = text.toLowerCase();
  const found = {};
  FILLER_PATTERNS.forEach((pat) => {
    const word = pat.replace(/\\b/g, "").trim();
    const matches = lower.match(new RegExp(pat, "g"));
    if (matches) found[word] = (found[word] || 0) + matches.length;
  });
  const total = Object.values(found).reduce((a, b) => a + b, 0);
  return { total, found };
}

function fmt(secs) {
  return `${Math.floor(secs / 60)}:${String(secs % 60).padStart(2, "0")}`;
}

// ── Score bar ─────────────────────────────────────────────────────────────────
function ScoreBar({ label, score, note }) {
  const pct = Math.round((score / 10) * 100);
  const color = score >= 8 ? "#D3FF24" : score >= 6 ? "#FFA116" : "#FF6B6B";
  return (
    <div className="flex items-center gap-3 py-1">
      <div className="text-[11px] text-neutral-400 w-32 shrink-0">{label}</div>
      <div className="flex-1 h-1 bg-white/[0.07] relative overflow-hidden">
        <div
          className="absolute left-0 top-0 h-full transition-all duration-700"
          style={{ width: `${pct}%`, background: color }}
        />
      </div>
      <div className="text-[12px] font-black w-6 text-right" style={{ color }}>
        {score}
      </div>
      <div className="text-[11px] text-neutral-500 w-36 truncate">{note}</div>
    </div>
  );
}

// ── Live metric chip ──────────────────────────────────────────────────────────
function Metric({ label, value, color }) {
  return (
    <div className="border border-white/[0.07] bg-white/[0.02] px-3 py-2 text-center">
      <div className="text-xl font-black font-mono leading-none mb-0.5" style={{ color }}>
        {value}
      </div>
      <div className="text-[9px] uppercase tracking-widest text-neutral-600">{label}</div>
    </div>
  );
}

// ── Main component ────────────────────────────────────────────────────────────
export default function SpeechReflectionPanel({ session, onClose }) {
  const [phase, setPhase] = useState("idle"); // idle | recording | analyzing | results
  const [transcript, setTranscript] = useState("");
  const [interimText, setInterimText] = useState("");
  const [wpm, setWpm] = useState(0);
  const [fillers, setFillers] = useState({ total: 0, found: {} });
  const [pauses, setPauses] = useState(0);
  const [duration, setDuration] = useState(0);
  const [analysis, setAnalysis] = useState(null);
  const [collapsed, setCollapsed] = useState(false);

  const recRef         = useRef(null);
  const audioCtxRef    = useRef(null);
  const audioStreamRef = useRef(null);
  const startTimeRef   = useRef(null);
  const timerRef       = useRef(null);
  const silenceRef     = useRef(false);
  const silenceTimerRef = useRef(null);
  const pauseCountRef  = useRef(0);
  const transcriptRef  = useRef("");
  const silenceCheckRef = useRef(null);

  const enabledChecks = Object.entries(session?.speech_reflection || {})
    .filter(([, v]) => v)
    .map(([k]) => k);

  // ── Duration timer ────────────────────────────────────────────────────────
  useEffect(() => {
    if (phase === "recording") {
      timerRef.current = setInterval(() => {
        const secs = Math.floor((Date.now() - startTimeRef.current) / 1000);
        setDuration(secs);
        const words = transcriptRef.current.trim().split(/\s+/).filter(Boolean).length;
        const mins = secs / 60;
        if (mins > 0.08) setWpm(Math.round(words / mins));
      }, 800);
    }
    return () => clearInterval(timerRef.current);
  }, [phase]);

  // ── Cleanup on unmount ────────────────────────────────────────────────────
  useEffect(() => () => teardown(), []);

  const teardown = () => {
    clearInterval(timerRef.current);
    clearInterval(silenceCheckRef.current);
    clearTimeout(silenceTimerRef.current);
    try { recRef.current?.stop(); } catch {}
    audioStreamRef.current?.getTracks().forEach((t) => t.stop());
    try { audioCtxRef.current?.close(); } catch {}
  };

  // ── Start ─────────────────────────────────────────────────────────────────
  const startRecording = async () => {
    const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SR) {
      toast.error("Speech recognition requires Chrome or Edge.");
      return;
    }

    try {
      // Mic access
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true, video: false });
      audioStreamRef.current = stream;

      // AudioContext — volume / silence detection
      const ctx = new (window.AudioContext || window.webkitAudioContext)();
      audioCtxRef.current = ctx;
      const analyser = ctx.createAnalyser();
      analyser.fftSize = 512;
      ctx.createMediaStreamSource(stream).connect(analyser);
      const freqData = new Uint8Array(analyser.frequencyBinCount);

      silenceCheckRef.current = setInterval(() => {
        analyser.getByteFrequencyData(freqData);
        const avg = freqData.reduce((a, b) => a + b, 0) / freqData.length;
        if (avg < 6) {
          if (!silenceRef.current) {
            silenceRef.current = true;
            silenceTimerRef.current = setTimeout(() => {
              if (silenceRef.current) {
                pauseCountRef.current += 1;
                setPauses(pauseCountRef.current);
              }
            }, 2000);
          }
        } else {
          silenceRef.current = false;
          clearTimeout(silenceTimerRef.current);
        }
      }, 200);

      // SpeechRecognition
      const rec = new SR();
      rec.continuous     = true;
      rec.interimResults = true;
      rec.lang           = "en-US";

      rec.onresult = (event) => {
        let finalChunk = "";
        let interim    = "";
        for (let i = event.resultIndex; i < event.results.length; i++) {
          if (event.results[i].isFinal) {
            finalChunk += event.results[i][0].transcript + " ";
          } else {
            interim += event.results[i][0].transcript;
          }
        }
        if (finalChunk) {
          transcriptRef.current += finalChunk;
          const f = detectFillers(transcriptRef.current);
          setTranscript(transcriptRef.current);
          setFillers(f);
        }
        setInterimText(interim);
      };

      rec.onerror = (e) => {
        if (e.error !== "no-speech") toast.error("Mic error: " + e.error);
      };
      rec.onend = () => {
        // Auto-restart if we're still in recording phase
        if (recRef.current === rec) {
          try { rec.start(); } catch {}
        }
      };

      rec.start();
      recRef.current = rec;

      // Reset state
      startTimeRef.current   = Date.now();
      pauseCountRef.current  = 0;
      transcriptRef.current  = "";
      setTranscript("");
      setInterimText("");
      setFillers({ total: 0, found: {} });
      setPauses(0);
      setWpm(0);
      setDuration(0);
      setAnalysis(null);
      setPhase("recording");
    } catch (e) {
      toast.error("Microphone access denied: " + e.message);
    }
  };

  // ── Stop & analyze ────────────────────────────────────────────────────────
  const stopAndAnalyze = async () => {
    setPhase("analyzing");
    recRef.current = null; // prevent auto-restart in onend
    teardown();

    const finalText    = transcriptRef.current.trim();
    const finalSecs    = Math.floor((Date.now() - startTimeRef.current) / 1000);
    const wordCount    = finalText.split(/\s+/).filter(Boolean).length;
    const finalWpm     = finalSecs > 5 ? Math.round(wordCount / (finalSecs / 60)) : 0;
    const finalFillers = detectFillers(finalText);

    setDuration(finalSecs);
    setWpm(finalWpm);
    setFillers(finalFillers);

    if (wordCount < 8) {
      toast.error("Too little speech to analyze — try speaking for at least 15 seconds.");
      setPhase("idle");
      return;
    }

    try {
      const { data } = await api.post("/speech/analyze", {
        transcript:      finalText,
        duration_seconds: finalSecs,
        wpm:             finalWpm,
        filler_count:    finalFillers.total,
        filler_words:    finalFillers.found,
        pause_count:     pauseCountRef.current,
        enabled_checks:  enabledChecks,
        session_id:      session?.id || "",
      });
      setAnalysis(data);
      setPhase("results");
    } catch (err) {
      toast.error("Analysis failed — " + (err.response?.data?.detail || err.message));
      setPhase("idle");
    }
  };

  const reset = () => {
    setPhase("idle");
    setTranscript("");
    setInterimText("");
    setAnalysis(null);
    setWpm(0);
    setFillers({ total: 0, found: {} });
    setPauses(0);
    setDuration(0);
  };

  const wpmColor     = wpm > 165 ? "#FF6B6B" : wpm < 100 && wpm > 0 ? "#FFA116" : "#D3FF24";
  const fillerColor  = fillers.total > 10 ? "#FF6B6B" : fillers.total > 5 ? "#FFA116" : "#D3FF24";

  return (
    <div
      className="fixed bottom-0 left-0 right-0 z-40 transition-transform duration-300"
      style={{ transform: collapsed ? "translateY(calc(100% - 44px))" : "translateY(0)" }}
    >
      <div className="bg-[#0D0D0D] border-t border-white/10 shadow-[0_-8px_40px_rgba(0,0,0,0.6)]">

        {/* ── Header bar (always visible) ── */}
        <div className="flex items-center justify-between px-6 h-11 border-b border-white/[0.06]">
          <div className="flex items-center gap-3">
            {phase === "recording" && (
              <span className="w-2 h-2 rounded-full bg-red-500 animate-pulse shrink-0" />
            )}
            <span className="text-[11px] uppercase tracking-widest font-bold text-[#D3FF24]">
              Speech Reflection
            </span>
            {phase === "recording" && (
              <span className="text-[11px] font-mono text-neutral-500">{fmt(duration)}</span>
            )}
            {phase === "results" && analysis && (
              <span className="text-[11px] text-neutral-500">
                {fmt(duration)} · {wpm} WPM · {fillers.total} fillers · {analysis.overall_score?.toFixed(1)}/10
              </span>
            )}
          </div>
          <div className="flex items-center gap-1">
            {phase === "results" && (
              <button
                onClick={reset}
                className="flex items-center gap-1.5 text-[10px] uppercase tracking-widest text-neutral-500 hover:text-white border border-white/10 px-2.5 py-1 transition mr-1"
              >
                <RotateCcw className="w-3 h-3" /> Retake
              </button>
            )}
            <button
              onClick={() => setCollapsed((c) => !c)}
              className="text-neutral-600 hover:text-neutral-300 transition p-1.5"
            >
              {collapsed ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
            </button>
            <button
              onClick={() => { teardown(); onClose(); }}
              className="text-neutral-600 hover:text-neutral-300 transition p-1.5"
            >
              <X className="w-3.5 h-3.5" />
            </button>
          </div>
        </div>

        {/* ── Body ── */}
        <div className="overflow-y-auto" style={{ maxHeight: 320 }}>

          {/* Idle */}
          {phase === "idle" && (
            <div className="flex flex-col items-center justify-center py-7 gap-4">
              <p className="text-sm text-neutral-500 text-center max-w-lg leading-relaxed">
                Record yourself answering the question aloud. Get real-time coaching on pace, filler words, confidence, grammar, tone, and delivery.
              </p>
              <button
                onClick={startRecording}
                className="flex items-center gap-2.5 bg-[#D3FF24] text-black px-8 py-3 text-xs font-bold uppercase tracking-widest hover:brightness-105 transition"
              >
                <Mic className="w-4 h-4" />
                Start Recording
              </button>
            </div>
          )}

          {/* Recording */}
          {phase === "recording" && (
            <div className="px-6 py-4 space-y-4">
              {/* Live metrics */}
              <div className="grid grid-cols-4 gap-2">
                <Metric label="WPM" value={wpm || "—"} color={wpmColor} />
                <Metric label="Fillers" value={fillers.total} color={fillerColor} />
                <Metric label="Pauses" value={pauses} color="#888" />
                <Metric label="Duration" value={fmt(duration)} color="#888" />
              </div>

              {/* Live transcript */}
              <div className="border border-white/[0.07] bg-black/30 px-4 py-3 min-h-[72px] max-h-[100px] overflow-y-auto">
                <div className="text-[9px] uppercase tracking-widest text-neutral-600 mb-1.5">
                  Live Transcript
                </div>
                <p className="text-sm text-neutral-300 leading-relaxed">
                  {transcript}
                  {interimText && (
                    <span className="text-neutral-600 italic"> {interimText}</span>
                  )}
                  {!transcript && !interimText && (
                    <span className="text-neutral-700 italic">Start speaking…</span>
                  )}
                </p>
              </div>

              {/* Filler word highlights */}
              {fillers.total > 0 && (
                <div className="flex flex-wrap gap-1.5">
                  {Object.entries(fillers.found).map(([word, count]) => (
                    <span
                      key={word}
                      className="text-[10px] border border-[#FFA116]/40 bg-[#FFA116]/[0.06] text-[#FFA116] px-2 py-0.5 font-mono"
                    >
                      "{word}" ×{count}
                    </span>
                  ))}
                </div>
              )}

              <div className="flex justify-center pb-1">
                <button
                  onClick={stopAndAnalyze}
                  className="flex items-center gap-2.5 border border-[#D3FF24]/40 text-[#D3FF24] px-8 py-2.5 text-xs font-bold uppercase tracking-widest hover:bg-[#D3FF24]/[0.05] transition"
                >
                  <MicOff className="w-4 h-4" />
                  Stop &amp; Analyze
                </button>
              </div>
            </div>
          )}

          {/* Analyzing */}
          {phase === "analyzing" && (
            <div className="flex flex-col items-center justify-center py-10 gap-3">
              <Loader2 className="w-5 h-5 animate-spin text-[#D3FF24]" />
              <p className="text-sm text-neutral-500">
                Analyzing your speech with AI…
              </p>
            </div>
          )}

          {/* Results */}
          {phase === "results" && analysis && (
            <div className="grid grid-cols-2 gap-0 divide-x divide-white/[0.06]">
              {/* Left: scores */}
              <div className="px-6 py-4 space-y-1">
                <div className="text-[9px] uppercase tracking-widest text-neutral-600 mb-3">
                  Performance Scores
                </div>
                {analysis.metrics &&
                  Object.values(analysis.metrics).map((m) => (
                    <ScoreBar key={m.label} label={m.label} score={m.score} note={m.note} />
                  ))}
                {/* Overall */}
                {analysis.overall_score != null && (
                  <div className="flex items-center gap-3 pt-2 border-t border-white/[0.06] mt-2">
                    <div className="text-[11px] text-white font-bold w-32">Overall</div>
                    <div className="flex-1 h-1 bg-white/[0.07] relative">
                      <div
                        className="absolute left-0 top-0 h-full bg-[#D3FF24] transition-all duration-700"
                        style={{ width: `${(analysis.overall_score / 10) * 100}%` }}
                      />
                    </div>
                    <div className="text-[12px] font-black text-[#D3FF24] w-6 text-right">
                      {analysis.overall_score.toFixed(1)}
                    </div>
                    <div className="w-36" />
                  </div>
                )}
              </div>

              {/* Right: coaching tips */}
              <div className="px-6 py-4">
                <div className="text-[9px] uppercase tracking-widest text-neutral-600 mb-3">
                  Coaching Tips
                </div>
                <div className="space-y-2">
                  {analysis.tips?.map((tip, i) => (
                    <div
                      key={i}
                      className="flex gap-2.5 border border-white/[0.06] bg-white/[0.01] px-3 py-2"
                    >
                      <span className="text-[#D3FF24] text-[11px] font-bold shrink-0 mt-0.5">›</span>
                      <p className="text-[11px] text-neutral-300 leading-relaxed">{tip}</p>
                    </div>
                  ))}
                  {(!analysis.tips || analysis.tips.length === 0) && (
                    <p className="text-[11px] text-neutral-600 italic">No specific tips generated.</p>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
